import { useEffect, useRef, useState } from 'react';

export interface MultiOption {
  value: string;
  label: string;
}

/**
 * Selector múltiple con checkboxes y cierre al hacer clic afuera.
 * Vacío = "sin filtro" (equivale a "todos"). Pensado para los filtros globales.
 */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  allLabel = 'Todos',
  minWidth,
}: {
  label: string;
  options: MultiOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  allLabel?: string;
  minWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const summary =
    selected.length === 0 ? allLabel : selected.length === 1
      ? options.find((o) => o.value === selected[0])?.label ?? selected[0]
      : `${selected.length} seleccionados`;

  return (
    <div className="multiselect" ref={ref} style={minWidth ? { minWidth } : undefined}>
      <label>{label}</label>
      <button
        type="button"
        className={`multiselect-trigger ${selected.length ? 'has-value' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="multiselect-summary">{summary}</span>
        <span className="multiselect-caret" aria-hidden>▾</span>
      </button>
      {open && (
        <div className="multiselect-panel" role="listbox" aria-multiselectable>
          <div className="multiselect-actions">
            <button type="button" className="ghost sm" onClick={() => onChange(options.map((o) => o.value))}>
              Todos
            </button>
            <button type="button" className="ghost sm" disabled={!selected.length} onClick={() => onChange([])}>
              Ninguno
            </button>
          </div>
          {options.length === 0 && <div className="muted" style={{ padding: '6px 8px', fontSize: 12 }}>Sin opciones</div>}
          {options.map((o) => (
            <label key={o.value} className="multiselect-option">
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
